import { Currency, CurrencyList } from "@common-components/payment-form/currency-codes";
import { BluesnapShopper } from "@common-models/bluesnap-shopper";
import { CancellationSurveyPayload, CancelReasonError, cancelReasonErrorOf } from "@common-models/cancellation";
import { selectAmountDue, staticSelectBillingInfo } from '@common-reducers/PaymentFormSelectors';
import { activeSubscriptionSelector } from '@common-reducers/UserSelectors';
import { createAsyncThunk, ThunkDispatch, AnyAction } from "@reduxjs/toolkit";
import { resolveListPrice, parseCatalogue, ListPrice } from "@common-services/checkout-pricing";

import { namespace } from '@common/config/constants';

import { CommonRootState } from "@common/types/common-root-state-type";
import * as postalCodes from 'postal-codes-js';
import { backendApiEndpoints, GetCheckoutPageUrlParams, GetCheckoutPageUrlResponse } from "./backend-api/backend-api";
import { DBSubscriptionThunks } from "@common-reducers/DBServiceThunks";
import { paymentFormActions } from './PaymentFormReducer';
import { resolveCheckoutErrorMessage } from './checkout-error-message';
import { useTranslation } from "react-i18next";
import { selectCurrentUser } from "./UserSelectors";
import { AnalyticsService } from "@common-services/analytics-service";
import { ProductEvent, trackProductEvent } from "@common-services/product-analytics";

export interface openPaymentDialogThunkParams {
    planId?: string;
    contractId?: string;
    workspaceId?: string;
    seats?: number;
    paymentSuccess?: boolean;
}

export const openPaymentDialogThunk = createAsyncThunk<void, openPaymentDialogThunkParams, { state: CommonRootState }>("openPaymentDialogThunk", async (params, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    const state = thunkApi.getState();

    const user = selectCurrentUser(state);

    document.title = 'Payment Form';

    dispatch(paymentFormActions.openDialog({
        ...params,
        user,
    }));

    const productId = params.planId;
    if (productId) {
        dispatch(calculateCartThunk())
    }
});


export const closePaymentDialogThunk = createAsyncThunk<void, void, { state: CommonRootState }>("closePaymentDialogThunk", async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    dispatch(paymentFormActions.setPaymentSuccessType(undefined));
    dispatch(paymentFormActions.setPaymentSuccess(false));
    dispatch(paymentFormActions.closeDialog());
});

export const calculateCartThunk = createAsyncThunk<void, void, { state: CommonRootState }>("calculateCartThunk", async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    let state = thunkApi.getState();
    const { planId, appliedCouponCode, seats } = state.PaymentFormReducer;

    console.log('Calculating cart');
    dispatch(paymentFormActions.setCalculatingCart(true));

    const estimate: Estimate = await dispatch(backendApiEndpoints.createEstimate.initiate({
        quantity: seats ?? 1,
        item_price_id: planId ?? '',
        coupon_ids: appliedCouponCode ? [appliedCouponCode] : [],
    })).unwrap();

    console.log(estimate);

    if (estimate) {
        dispatch(paymentFormActions.setEstimate(estimate));
    } else {
        dispatch(paymentFormActions.setCartErrorMessage('Error calculating cart'));
    }

    console.log('Done calculating cart');
    dispatch(paymentFormActions.setCalculatingCart(false));
});

export const createPaymentIntentThunk = createAsyncThunk<void, void, { state: CommonRootState }>("createPaymentIntentThunk", async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    let state = thunkApi.getState();
    const { seats } = state.PaymentFormReducer;
    const amountDue = selectAmountDue(state);
    const paymentMethod = state.PaymentFormReducer.paymentMethod ?? 'CreditCard';

    const payment_method_type = paymentMethod === 'CreditCard' ? 'card' : 'paypal_express_checkout';

    const paymentIntent = await dispatch(backendApiEndpoints.createIntent.initiate({
        amount: amountDue,
        payment_method_type,
        quantity: seats ?? 1,
    })).unwrap();
    dispatch(paymentFormActions.setPaymentIntent(paymentIntent));
});

export const setAppliedCouponCodeThunk = createAsyncThunk<void, string, { state: CommonRootState }>("setAppliedCouponCodeThunk", async (couponCode, thunkApi) => {
    const dispatch = thunkApi.dispatch;

    dispatch(paymentFormActions.setAppliedCouponCode(couponCode));
    dispatch(calculateCartThunk());
});

export const setCurrencyThunk = createAsyncThunk<void, string, { state: CommonRootState }>("setCurrencyThunk", async (currencyCode, thunkApi) => {
    const dispatch = thunkApi.dispatch;

    const currency = CurrencyList.find(currency => currency.code === currencyCode);
    if (!currency) return;

    dispatch(paymentFormActions.setCurrency(currency));
    dispatch(calculateCartThunk());
});


export const validateVATNumberThunk = createAsyncThunk<void, string | undefined, { state: CommonRootState }>("validateVATNumberThunk", async (vatIdInput, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    const state = thunkApi.getState();
    const { country } = state.PaymentFormReducer;
    const { vatId } = state.PaymentFormReducer;

    const chargebee = Chargebee.getInstance();

    if (!country) return;
    const result = await chargebee.vat.validateVat({
        country: country.code,
        vat_number: vatIdInput ?? vatId
    });
    dispatch(paymentFormActions.setVatValidation({
        vatStatus: result.status,
        message: result.message
    }));
});



export const validateZipCodeThunk = createAsyncThunk(
    'paymentForm/validateZipCode',
    async (_, { getState }) => {
        const state = getState() as CommonRootState;
        const { zip, country } = state.PaymentFormReducer;

        if (!zip || !country?.code) {
            return { isValid: false, message: 'Please enter a zip code and select a country.' };
        }

        const validationResult = postalCodes.validate(country.code, zip);

        if (validationResult === true) {
            return { isValid: true, message: '' };
        } else if (typeof validationResult === 'string') {
            return { isValid: false, message: validationResult };
        } else {
            // Handle the case where validationResult is false
            return { isValid: false, message: `Invalid postal code for ${country.code}` };
        }
    }
);

// Thunk to fetch shopper/customer information
export const getShopperInfoThunk = createAsyncThunk(
    'paymentForm/getShopperInfo',
    async (_, thunkApi) => {
        const dispatch = thunkApi.dispatch;
        const state = thunkApi.getState() as CommonRootState;
        const workspaceSubscription = activeSubscriptionSelector(state);
        const data = await thunkApi.dispatch(backendApiEndpoints.getShopperInfo.initiate()).unwrap();

        let shopper: BluesnapShopper | undefined;
        let customer: Customer | undefined;
        const { paymentSourceList } = data;
        if (workspaceSubscription?.type === 'bluesnap') {
            shopper = data.shopper;
        } else {
            customer = data.customer;
        }
        const user = selectCurrentUser(state);

        const {
            firstName,
            lastName,
            address1,
            address2,
            zip,
            countryObject,
            companyName,
            vatId,
        } = staticSelectBillingInfo(user, workspaceSubscription, shopper, customer, paymentSourceList);

        return {
            data,
            type: workspaceSubscription?.type,
            firstName,
            lastName,
            address1,
            address2,
            zip,
            countryObject,
            companyName,
            vatId,
        };
    }
);

/**
 * Leave for the hosted checkout. If the user comes BACK (browser Back from Chargebee), the page is
 * usually restored from the back/forward cache with the whole JS heap frozen as it was — including
 * isGettingPaymentUrl=true — and nothing ever clears it, so the plans page sat on its loading state
 * forever (a blank page). `pageshow` with `persisted` fires exactly on that restore.
 */
const redirectToCheckout = (url: string, dispatch: (action: unknown) => unknown) => {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) dispatch(paymentFormActions.setIsGettingPaymentUrl(false));
    }, { once: true });
    window.location.href = url;
};

/** Catalogue list price for an analytics event; null on any failure (analytics must never block checkout). */
async function fetchListPrice(dispatch: ThunkDispatch<unknown, unknown, AnyAction>, itemPriceId: string, quantity: number): Promise<ListPrice | null> {
    try {
        const prices = await dispatch(backendApiEndpoints.getItemPrices.initiate()).unwrap();
        return resolveListPrice(parseCatalogue(prices), itemPriceId, quantity);
    } catch {
        return null;
    }
}

// Get checkout page url thunk
export const openCheckoutPageThunk = createAsyncThunk<void, GetCheckoutPageUrlParams>(
    'paymentForm/openCheckoutPageThunk',
    async (params, thunkApi) => {
        const dispatch = thunkApi.dispatch;
        const analyticsService = new AnalyticsService();
        
        try {
            dispatch(paymentFormActions.setIsGettingPaymentUrl(true));
            dispatch(paymentFormActions.clearCheckoutErrorMessage());

            // Extract plan information from params
            const planId = params.item_price_id || '';
            const planName = planId.split('-')[0] || 'Unknown'; // Extract plan name from item_price_id like "Pro-USD-Monthly"
            const currency = planId.includes('USD') ? 'USD' : planId.includes('EUR') ? 'EUR' : 'USD';
            const billingCycle = planId.includes('Monthly') ? 'Monthly' : planId.includes('Yearly') ? 'Yearly' : 'Monthly';
            const quantity = params.quantity || 1;
            
            // List price for the analytics value (was hard-coded 0). Unknown -> value omitted, never 0.
            const listPrice = await fetchListPrice(thunkApi.dispatch, planId, quantity);
            const eventCurrency = listPrice?.currency ?? currency;

            // Create item structure for ecommerce events
            const ecommerceItems = [{
                item_id: planId,
                item_name: planName,
                item_category: 'subscription',
                item_brand: 'Blueticks',
                ...(listPrice && { price: Number((listPrice.value / quantity).toFixed(2)) }),
                quantity: quantity
            }];

            // Track add_to_cart event
            await analyticsService.ecommerceEvent('add_to_cart', {
                currency: eventCurrency,
                ...(listPrice && { value: listPrice.value }),
                items: ecommerceItems,
                coupon: params.coupon_ids?.join(',') || undefined
            }, {
                billing_cycle: billingCycle,
                auto_detect_billing_cycle: params.auto_detect_billing_cycle
            });
            
            // Track begin_checkout event
            await analyticsService.ecommerceEvent('begin_checkout', {
                currency: eventCurrency,
                ...(listPrice && { value: listPrice.value }),
                items: ecommerceItems,
                coupon: params.coupon_ids?.join(',') || undefined
            }, {
                checkout_step: 1,
                checkout_option: 'chargebee_hosted',
                billing_cycle: billingCycle
            });
            
            // Fetch fresh GA4 session_id (sessions rotate after 30 min of inactivity)
            // so the server-side purchase event joins this browser's current session
            const freshSessionId = await analyticsService.getGA4SessionId();
            const gaClientId = await analyticsService.getGA4ClientId();

            const checkoutParams = {
                ...params,
                ...(freshSessionId && { ga_session_id: freshSessionId }),
                ...(gaClientId && { ga_client_id: gaClientId }),
            };

            // Get checkout URL and redirect
            const { checkoutPageUrl } = await thunkApi.dispatch(backendApiEndpoints.getCheckoutPageUrl.initiate(checkoutParams)).unwrap();
            redirectToCheckout(checkoutPageUrl, dispatch);

        } catch (error) {
            console.error('Error in checkout process:', error);
            // Ensure checkout continues even if analytics fails — still pass GA4 data for session linking
            try {
                const fallbackParams = {
                    ...params,
                    ...(analyticsService.ga4SessionId && { ga_session_id: analyticsService.ga4SessionId }),
                    ...(analyticsService.ga4ClientId && { ga_client_id: analyticsService.ga4ClientId }),
                };
                const { checkoutPageUrl } = await thunkApi.dispatch(backendApiEndpoints.getCheckoutPageUrl.initiate(fallbackParams)).unwrap();
                redirectToCheckout(checkoutPageUrl, dispatch);
            } catch (checkoutError) {
                console.error('Checkout failed:', checkoutError);
                dispatch(paymentFormActions.setIsGettingPaymentUrl(false));
                // Tell the user. Previously this only reached the console, so the Upgrade button
                // just stopped spinning and the customer was left with a dead page.
                dispatch(paymentFormActions.setCheckoutErrorMessage(resolveCheckoutErrorMessage(checkoutError)));
                throw checkoutError;
            }
        }
    }
);

// Get checkout page url without redirect
export const getCheckoutPageUrlThunk = createAsyncThunk<GetCheckoutPageUrlResponse, GetCheckoutPageUrlParams>(
    'paymentForm/getCheckoutPageUrlThunk',
    async (params, thunkApi) => {
        const dispatch = thunkApi.dispatch;
        dispatch(paymentFormActions.setIsGettingPaymentUrl(true));
        const response = await thunkApi.dispatch(backendApiEndpoints.getCheckoutPageUrl.initiate(params)).unwrap();
        return {
            checkoutPageUrl: response.checkoutPageUrl,
            debug: response.debug || {
                function: 'checkout_new_for_items',
                input: params
            }
        };
    }
);

export const getPortalSessionUrlThunk = createAsyncThunk<string, void, { state: CommonRootState }>(
    'paymentForm/getPortalSessionUrlThunk',
    async (_, thunkApi) => {
        const { portal_session_url } = await thunkApi.dispatch(backendApiEndpoints.getPortalSessionUrl.initiate()).unwrap();
        return portal_session_url;
    }
);


// Cancel active subscription thunk.
// BE#498 — accepts the structured cancel survey (reasonCode/detail + save-offer
// outcome) and forwards it to the backend so it is recorded durably. Still accepts
// the legacy `{ reason }` (free-text) shape for back-compat.
export const cancelSubscriptionThunk = createAsyncThunk<void, { reason?: string; cancellation?: CancellationSurveyPayload } | void, { state: CommonRootState; rejectValue: CancelReasonError }>(
    'paymentForm/cancelSubscriptionThunk',
    async (arg, thunkApi) => {
        const cancellation: CancellationSurveyPayload | undefined = arg && 'cancellation' in arg ? arg.cancellation : undefined;
        // Legacy free-text reason folds into reasonDetail if a structured survey wasn't passed.
        const legacyReason = arg && 'reason' in arg ? (arg.reason ?? '') : '';
        const survey: CancellationSurveyPayload | undefined = cancellation
            ?? (legacyReason ? { reasonDetail: legacyReason } : undefined);

        // FE#194 ask-why: record why the customer is cancelling BEFORE the cancel call,
        // so the reason survives even if the network cancel is slow. Best-effort only —
        // analytics must never block or fail the cancellation.
        try {
            trackProductEvent(ProductEvent.CancelSubscription, {
                category: 'Subscription',
                action: 'cancel subscription',
                reason: survey?.reasonCode ?? legacyReason,
                reasonDetail: survey?.reasonDetail,
                offerShown: survey?.offerShown,
                offerOutcome: survey?.offerOutcome,
            });
        } catch {
            /* never break the cancel path over analytics */
        }

        const res = await thunkApi.dispatch(backendApiEndpoints.cancelActiveSubscription.initiate(
            survey ? { cancellation: survey } : undefined,
        ));
        // #241 — CANCEL_REASON_REQUIRED: the server refused because the reason is missing.
        // Reject with the field so the dialog sends the customer back to the question.
        const reasonError = cancelReasonErrorOf('error' in res ? res.error : undefined);
        if (reasonError) return thunkApi.rejectWithValue(reasonError);

        // Refresh the user's subscription status
        await thunkApi.dispatch(DBSubscriptionThunks.find({}));
    }
);

// BE#498 — pause save offer (Chargebee native pause, auto-resume in N months).
export const pauseSubscriptionThunk = createAsyncThunk<{ success: boolean; paused: number; failed: number; resumeInMonths: number }, { resumeInMonths?: number } | void, { state: CommonRootState }>(
    'paymentForm/pauseSubscriptionThunk',
    async (arg, thunkApi) => {
        const res = await thunkApi.dispatch(backendApiEndpoints.pauseSubscription.initiate(
            arg && 'resumeInMonths' in arg ? { resumeInMonths: arg.resumeInMonths } : undefined,
        )).unwrap();
        await thunkApi.dispatch(DBSubscriptionThunks.find({}));
        return res;
    }
);

// BE#498 — discount save offer (capped-40% coupon applied to active subs).
export const applySaveOfferDiscountThunk = createAsyncThunk<{ success: boolean; couponId: string; discountPercentage: number; applied: number; failed: number }, { discountPct: number; durationMonths?: number; reasonCode?: string; reasonDetail?: string }, { state: CommonRootState }>(
    'paymentForm/applySaveOfferDiscountThunk',
    async (params, thunkApi) => {
        const res = await thunkApi.dispatch(backendApiEndpoints.applySaveOfferDiscount.initiate(params)).unwrap();
        await thunkApi.dispatch(DBSubscriptionThunks.find({}));
        return res;
    }
);

// #241 — accept the annual save: the server mints the coupon + hosted checkout, then we leave for it.
// Rejects (with the server's BadRequest) when the offer isn't available, so the dialog can say so.
export const startAnnualSaveCheckoutThunk = createAsyncThunk<void, { reasonCode?: string; reasonDetail?: string; source?: 'cancel_flow' | 'renewal_email' }, { state: CommonRootState }>(
    'paymentForm/startAnnualSaveCheckoutThunk',
    async (params, thunkApi) => {
        const { checkoutPageUrl } = await thunkApi.dispatch(backendApiEndpoints.startAnnualSaveCheckout.initiate(params)).unwrap();
        redirectToCheckout(checkoutPageUrl, thunkApi.dispatch);
    }
);

// BE#498 — durably record a cancel-flow dismiss ("Keep my subscription" / reconsidered).
// Fire-and-forget: a flow SUCCESS signal that must never block or fail the close.
export const recordCancelFlowDismissThunk = createAsyncThunk<void, { reasonCode?: string; reasonDetail?: string; offerShown?: string; step?: string }, { state: CommonRootState }>(
    'paymentForm/recordCancelFlowDismissThunk',
    async (params, thunkApi) => {
        try {
            await thunkApi.dispatch(backendApiEndpoints.recordCancelFlowDismiss.initiate(params)).unwrap();
        } catch {
            /* best-effort observability — never surface to the user */
        }
    }
);